<template>
  <DialogModal
    max-width="md"
    :show="show"
    @close="closeDialog"
  >
    <template #title>
      <div class="flex w-full flex-col gap-y-4">
        <div class="flex items-center justify-between">
          <div class="flex size-12 items-center justify-center rounded-[28px] border-[8px] border-primary-100 bg-primary-200">
            <CalendarIcon class="size-5 text-primary-700" />
          </div>
          <Button
            variant="ghost"
            @click="closeDialog"
          >
            <XIcon class="size-6 text-gray-600" />
          </Button>
        </div>
        <span class="text-lg font-semibold text-gray-900">
          {{ submitted ? "Thanks for reaching out!" : "Book a demo" }}
        </span>
        <span class="text-sm font-normal text-gray-600">
          {{
            submitted
              ? "We received your request and will be in touch shortly to schedule your demo."
              : "Tell us a bit about you and we'll get in touch to schedule a demo."
          }}
        </span>
      </div>
    </template>

    <template #content>
      <div
        v-if="submitted"
        class="text-start text-sm text-gray-600"
      >
        In the meantime, feel free to reach us at
        <a
          href="mailto:hello@designertool.io"
          class="font-medium text-primary-700"
        >hello@designertool.io</a>.
      </div>

      <form
        v-else
        id="book-demo-form"
        class="flex flex-col gap-y-4 text-start"
        @submit.prevent="submitForm"
      >
        <div class="flex flex-col gap-y-1.5">
          <Label for="demo-name">Name</Label>
          <Input
            id="demo-name"
            v-model="form.name"
            type="text"
            placeholder="Your name"
            class="h-10 flex-1"
          />
          <InputError :message="form.errors.name" />
        </div>

        <div class="flex flex-col gap-y-1.5">
          <Label for="demo-email">Work email</Label>
          <Input
            id="demo-email"
            v-model="form.email"
            type="email"
            placeholder="you@company.com"
            class="h-10 flex-1"
          />
          <InputError :message="form.errors.email" />
        </div>

        <div class="flex flex-col gap-y-1.5">
          <Label for="demo-company">Company <span class="text-gray-400">(optional)</span></Label>
          <Input
            id="demo-company"
            v-model="form.company"
            type="text"
            placeholder="Company name"
            class="h-10 flex-1"
          />
          <InputError :message="form.errors.company" />
        </div>

        <div class="flex flex-col gap-y-1.5">
          <Label for="demo-message">Message <span class="text-gray-400">(optional)</span></Label>
          <textarea
            id="demo-message"
            v-model="form.message"
            rows="3"
            placeholder="What would you like to see?"
            class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          />
          <InputError :message="form.errors.message" />
        </div>
      </form>
    </template>

    <template #footer>
      <div
        v-if="submitted"
        class="grid w-full grid-cols-1"
      >
        <Button
          type="button"
          variant="default"
          @click="closeDialog"
        >
          Close
        </Button>
      </div>
      <div
        v-else
        class="grid w-full grid-cols-2 gap-x-3"
      >
        <Button
          type="button"
          variant="outline"
          @click="closeDialog"
        >
          Cancel
        </Button>
        <Button
          form="book-demo-form"
          type="submit"
          variant="default"
          :disabled="form.processing"
        >
          <span v-if="form.processing">Sending...</span>
          <span v-else>Request demo</span>
        </Button>
      </div>
    </template>
  </DialogModal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { route } from "ziggy-js";
import { CalendarIcon, XIcon } from "lucide-vue-next";
import { useForm } from "@inertiajs/vue3";
import DialogModal from "@/Components/DialogModal.vue";
import InputError from "@/Components/InputError.vue";
import Input from "@/Components/ui/input/Input.vue";
import { Label } from "@/Components/ui/label";
import Button from "@/Components/ui/button/Button.vue";

defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();

const submitted = ref(false);

const form = useForm({
  name: "",
  email: "",
  company: "",
  message: "",
});

function closeDialog() {
  emit("update:show", false);
  // Reset after the close transition so the form doesn't flicker while hiding.
  setTimeout(() => {
    submitted.value = false;
    form.reset();
    form.clearErrors();
  }, 200);
}

function submitForm() {
  form.post(route("demo.request"), {
    preserveScroll: true,
    onSuccess: () => {
      submitted.value = true;
      form.reset();
    },
  });
}
</script>
